import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { I18nContext, translate } from '@/i18n';
import { ThemeContext } from '@/app/theme';
import {
  detectBrowserLocale,
  getLocalePreference,
  getThemePreference,
  setLocalePreference,
  setThemePreference,
  type LocaleCode,
  type ThemePreference,
} from '@/shared/storage';

function applyTheme(theme: ThemePreference) {
  const root = document.documentElement;
  if (theme === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', theme);
  }
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(
    () => getLocalePreference() ?? detectBrowserLocale(),
  );
  const [theme, setThemeState] = useState<ThemePreference>(() => getThemePreference());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = locale === 'pt' ? 'pt-BR' : 'en';
  }, [locale]);

  const setLocale = useCallback((next: LocaleCode) => {
    setLocalePreference(next);
    setLocaleState(next);
  }, []);

  const setTheme = useCallback((next: ThemePreference) => {
    setThemePreference(next);
    setThemeState(next);
  }, []);

  const i18nValue = useMemo(
    () => ({
      locale,
      setLocale,
      t: (key: Parameters<typeof translate>[1], vars?: Record<string, string>) =>
        translate(locale, key, vars),
    }),
    [locale, setLocale],
  );

  const themeValue = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return (
    <I18nContext.Provider value={i18nValue}>
      <ThemeContext.Provider value={themeValue}>{children}</ThemeContext.Provider>
    </I18nContext.Provider>
  );
}
